import type {
  Bericht,
  Freigabeurteil,
  Messwerte,
  Nachweispruefung,
  Rechenantwort,
} from './typen'
import type { Zustandsart } from './Zustand'
import type { Bezug, Bezugsraum, Formelbefund, Knoten } from './Formel'
import { bezuegeIn, eingesetzt, istFehler, istAuswertungsfehler, lies, werteAus } from './Formel'
import { beschriftung, betrag, t, zahlenschema, anzahlwort } from './sprache'
import type { Sprachschluessel } from './sprache'

/**
 * Das Kachelbrett — Modell, Vorrat und Anordnung (7.5, R337–R349).
 *
 * **Was hier nicht steht, ist der eigentliche Inhalt dieser Datei:** Es gibt
 * keine Kachelart ohne Zahl und keine ohne Ziel (R347). Eine Kachel, die bloß
 * schmückt, lässt sich nicht anlegen — nicht weil es verboten wäre, sondern
 * weil der Vorrat sie nicht kennt. Das ist die Eigenschaft, die die
 * Arbeitsliste zur Arbeitsliste macht (R140), und sie gilt auch für das, was
 * der Anwender sich selbst zusammenstellt.
 *
 * Die Anordnung liegt im Prototyp im Browser des Anwenders. **Der Ort in der
 * Datenbank steht fest und heißt `core.anwenderbrett`**; er kommt mit der
 * Named-User-Verwaltung aus Bereich 8. Bis dahin überlebt eine Anordnung
 * keinen Rechnerwechsel — das ist benannt (7.5 Abschnitt 7) und nicht
 * versehentlich.
 */

/* --- Der Vorrat ----------------------------------------------------------- */

/** Drei Arten, fest wie der Bereichsvorrat (R338). */
export type Kachelart = 'zahl' | 'verlauf' | 'stand'

export const KACHELARTEN: readonly Kachelart[] = ['zahl', 'verlauf', 'stand']

/** Der Gegenstand einer Standkachel — ebenfalls ein Vorrat, keine freie Liste. */
export type Gegenstand = 'schritte' | 'lieferstand' | 'systemzustand'

export const GEGENSTAENDE: readonly Gegenstand[] = ['schritte', 'lieferstand', 'systemzustand']

/** Viertel, Drittel, Hälfte, volle Breite — zwölf Spalten (R343). */
export type Breite = 3 | 4 | 6 | 12

export const BREITEN: readonly Breite[] = [3, 4, 6, 12]

export type Hoehe = 1 | 2

/** Wie eine Zahl zu lesen ist. Ein Betrag ist keine Anzahl. */
export type Einheitsart = 'betrag' | 'anzahl' | 'ms' | 'prozent'

export type Sprung = { bereich: string; unterpunkt?: string }

/**
 * Woher die Zahl kommt — gestellt oder gerechnet (7.5 Abschnitt 3.3).
 *
 * **Gestellt** heißt: Sie steht schon im Modell, dieselbe Zahl, die auch die
 * Arbeitsliste einsammelt. **Formel** heißt: Der Anwender hat sie sich aus
 * gestellten Werten selbst gebaut — als Anzeige und nie als Abschluss (R339).
 */
export type Quelle =
  | { art: 'gestellt'; schluessel: string }
  | { art: 'formel'; ausdruck: string }

export type Herkunft =
  | { folgtKontext: true }
  | { folgtKontext: false; kreis: string; ledger: string; periode: number }

export type Schwellen = {
  /** Ab diesem Wert grün. `null` heißt: keine Ampel, die Kachel ist neutral. */
  gruenAb: number | null
  gelbAb: number | null
  /** Ob ein hoher Wert gut ist (Eigenkapitalquote) oder ein niedriger (Differenzen). */
  richtung: 'hoch' | 'niedrig'
}

export type Kachel = {
  id: string
  art: Kachelart
  /** `null` heißt: die Beschriftung kommt aus dem Vorrat, nicht aus der Hand. */
  titel: string | null
  quelle: Quelle
  gegenstand: Gegenstand | null
  einheit: Einheitsart
  darstellung: 'balken' | 'linie'
  /** Wie viele Zeiteinheiten ein Verlauf zeigt. */
  einheiten: number
  breite: Breite
  hoehe: Hoehe
  eingeklappt: boolean
  herkunft: Herkunft
  schwellen: Schwellen
}

export type Brett = {
  /** Damit eine gemerkte Anordnung eine spätere Ausgabe überlebt oder ausfällt. */
  fassung: 1
  kacheln: Kachel[]
}

/* --- Die gestellten Größen ------------------------------------------------ */

/**
 * Was das Modell ohnehin führt — die Zahlen, die keine Rechnung brauchen.
 *
 * Jede trägt ihr **Ziel** mit: Wohin man geht, um die Sache zu erledigen. Das
 * ist keine Zutat, sondern die Bedingung dafür, dass sie überhaupt eine Kachel
 * werden darf (R347).
 */
export type Groesse = {
  schluessel: string
  schriftzug: Sprachschluessel
  einheit: Einheitsart
  ziel: Sprung
  /** Wo ein hoher Wert schlecht ist, steht die Ampel andersherum. */
  richtung: 'hoch' | 'niedrig'
}

export const GROESSEN: readonly Groesse[] = [
  {
    schluessel: 'befunde.hart',
    schriftzug: 'kachel.groesse.befunde',
    einheit: 'anzahl',
    ziel: { bereich: 'abschluss', unterpunkt: 'monitor' },
    richtung: 'niedrig',
  },
  {
    schluessel: 'bilanz.differenz',
    schriftzug: 'kachel.groesse.bilanzdifferenz',
    einheit: 'betrag',
    ziel: { bereich: 'berichte', unterpunkt: 'bilanz' },
    richtung: 'niedrig',
  },
  {
    schluessel: 'nachweis.eintraege',
    schriftzug: 'kachel.groesse.nachweis',
    einheit: 'anzahl',
    ziel: { bereich: 'nachweis', unterpunkt: 'aenderungsnachweis' },
    richtung: 'hoch',
  },
  {
    schluessel: 'antwortzeit.p95',
    schriftzug: 'kachel.groesse.antwortzeit',
    einheit: 'ms',
    ziel: { bereich: 'betrieb', unterpunkt: 'antwortzeiten' },
    richtung: 'niedrig',
  },
  {
    schluessel: 'lauf.buchungen',
    schriftzug: 'kachel.groesse.buchungen',
    einheit: 'anzahl',
    ziel: { bereich: 'nachweis', unterpunkt: 'buchungsjournal' },
    richtung: 'hoch',
  },
  {
    schluessel: 'lauf.vorgaenge',
    schriftzug: 'kachel.groesse.vorgaenge',
    einheit: 'anzahl',
    ziel: { bereich: 'abschluss', unterpunkt: 'konzernabschluesse' },
    richtung: 'hoch',
  },
]

export function findeGroesse(schluessel: string): Groesse | null {
  return GROESSEN.find((g) => g.schluessel === schluessel) ?? null
}

/* --- Der Bezugsraum ------------------------------------------------------- */

/**
 * Ein Bezug mit allem, was die Kachel über ihn wissen muss.
 *
 * `Formel.Bezug` kennt nur Name, Wert und Währung — mehr braucht das Rechnen
 * nicht. Ziel und Zustand gehören der Anzeige, und deshalb stehen sie hier und
 * nicht dort.
 */
export type Kachelbezug = Bezug & {
  einheit: Einheitsart
  zustand: Zustandsart
  ziel: Sprung | null
  /** Für den Sprung in den Wertenachweis (R213/R342). */
  nachweis: { art: 'bilanz' | 'guv'; position: string } | null
  richtung: 'hoch' | 'niedrig'
}

export type Kachelraum = {
  bezuege: Record<string, Kachelbezug>
  lauf: Rechenantwort | null
  waehrung: string
  kreis: string
  ledger: string
  periode: number
}

/**
 * Sammelt ein, was für diese Herkunft bereits da ist — **es wird nichts
 * nachgerechnet** (R349).
 *
 * Der Bezugsraum entsteht aus denselben Antworten, aus denen die Arbeitsliste
 * ihre Zeilen zieht: dem Lauf, der Freigabeprüfung, den beiden Berichten, der
 * Nachweisprüfung und der Messung. Für die Kachel, die der Kontextzeile folgt,
 * kostet das **keinen einzigen zusätzlichen Abruf**.
 */
export function kachelraum(quellen: {
  lauf: Rechenantwort | null
  urteil: Freigabeurteil | null
  bilanz: Bericht | null
  guv: Bericht | null
  pruefung: Nachweispruefung | null
  messung: Messwerte | null
  kreis: string
  ledger: string
  periode: number
}): Kachelraum {
  const { lauf, urteil, bilanz, guv, pruefung, messung } = quellen
  const waehrung = bilanz?.waehrung ?? guv?.waehrung ?? lauf?.konzernwaehrung ?? ''
  const bezuege: Record<string, Kachelbezug> = {}

  const setze = (
    schluessel: string,
    name: string,
    wert: number,
    einheit: Einheitsart,
    zustand: Zustandsart,
    ziel: Sprung | null,
    richtung: 'hoch' | 'niedrig',
    nachweis: { art: 'bilanz' | 'guv'; position: string } | null = null,
  ): void => {
    bezuege[schluessel] = {
      schluessel,
      name,
      wert,
      waehrung: einheit === 'betrag' ? waehrung : null,
      einheit,
      zustand,
      ziel,
      nachweis,
      richtung,
    }
  }

  if (urteil) {
    const g = findeGroesse('befunde.hart')!
    setze(
      g.schluessel,
      t(g.schriftzug),
      urteil.befunde.length,
      'anzahl',
      urteil.befunde.length > 0 ? 'blockiert' : 'fertig',
      g.ziel,
      g.richtung,
    )
  }

  if (bilanz) {
    const g = findeGroesse('bilanz.differenz')!
    setze(
      g.schluessel,
      t(g.schriftzug),
      bilanz.differenz,
      'betrag',
      bilanz.gehtAuf ? 'fertig' : 'blockiert',
      g.ziel,
      g.richtung,
    )
  }

  if (pruefung) {
    const g = findeGroesse('nachweis.eintraege')!
    setze(
      g.schluessel,
      t(g.schriftzug),
      pruefung.eintraege,
      'anzahl',
      pruefung.unversehrt ? 'fertig' : 'blockiert',
      g.ziel,
      g.richtung,
    )
  }

  if (messung) {
    const g = findeGroesse('antwortzeit.p95')!
    setze(
      g.schluessel,
      t(g.schriftzug),
      messung.p95Ms,
      'ms',
      messung.p95Ms <= messung.budgetMs ? 'fertig' : 'offen',
      g.ziel,
      g.richtung,
    )
  }

  if (lauf) {
    const b = findeGroesse('lauf.buchungen')!
    setze(b.schluessel, t(b.schriftzug), lauf.buchungen, 'anzahl', 'fertig', b.ziel, b.richtung)

    const v = findeGroesse('lauf.vorgaenge')!
    setze(v.schluessel, t(v.schriftzug), lauf.vorgaenge, 'anzahl', 'fertig', v.ziel, v.richtung)
  }

  // Die Positionen beider Berichte. **Der Bezug ist ein Name, keine
  // Koordinate** (R340): Er lautet `bilanz.POS-A` und nicht „Zeile 7" — wird
  // umgegliedert, wandert er mit, und verschwindet die Position, sagt die
  // Kachel das im Klartext statt still auf null zu fallen.
  //
  // **Der Schlüssel und nicht der Gliederungsbuchstabe:** Seit Aktiva und
  // Passiva Knoten sind (R332), gibt es ein „A" unter jeder der beiden Seiten
  // — `bilanz.A` träfe das Anlagevermögen und das Eigenkapital zugleich. Der
  // Positionsschlüssel ist eindeutig, der Buchstabe ist es nie gewesen.
  for (const [art, bericht] of [
    ['bilanz', bilanz],
    ['guv', guv],
  ] as const) {
    for (const zeile of bericht?.zeilen ?? []) {
      setze(
        `${art}.${zeile.position}`,
        beschriftung(zeile.namen) || zeile.code,
        zeile.konzern,
        'betrag',
        'fertig',
        { bereich: 'berichte', unterpunkt: art },
        'hoch',
        { art, position: zeile.position },
      )
    }
  }

  return {
    bezuege,
    lauf,
    waehrung,
    kreis: quellen.kreis,
    ledger: quellen.ledger,
    periode: quellen.periode,
  }
}

/** Was die Formel davon braucht — Name, Wert, Währung. Mehr sieht sie nicht. */
export function formelraum(raum: Kachelraum): Bezugsraum {
  const raus: Bezugsraum = {}
  for (const [schluessel, b] of Object.entries(raum.bezuege)) {
    raus[schluessel] = { schluessel, name: b.name, wert: b.wert, waehrung: b.waehrung }
  }

  return raus
}

/* --- Der Wert einer Kachel ------------------------------------------------ */

export type Kachelwert = {
  roh: number
  text: string
  zustand: Zustandsart
  ziel: Sprung | null
  /** Die Rechnung mit eingesetzten Zahlen — nur bei einer Formel (R342). */
  rechnung: string | null
  /** Die Bezüge, die darin vorkommen — jeder führt in den Wertenachweis. */
  operanden: Kachelbezug[]
}

export type Kachelbefund = { fehler: Formelbefund }

export function istKachelbefund(w: Kachelwert | Kachelbefund): w is Kachelbefund {
  return 'fehler' in w
}

/**
 * Was die Kachel anzeigt — oder warum nicht.
 *
 * **Der Fehlerfall ist hier kein Sonderweg**, sondern der zweite gleichwertige
 * Ausgang: Eine Kachel, deren Position es nicht mehr gibt, sagt das mit Namen
 * (R340) und bleibt stehen. Sie verschwindet nicht von selbst vom Brett.
 */
export function werteKachel(kachel: Kachel, raum: Kachelraum): Kachelwert | Kachelbefund {
  /*
    **Ein leerer Bezugsraum ist kein fehlender Bezug.**

    Solange nichts geladen ist — und für eine Periode ohne Lauf —, kennt der
    Raum überhaupt keine Bezüge. „Den Bezug ,Anlagevermögen' gibt es in diesem
    Bericht nicht" wäre dann **eine falsche Aussage**: Es gibt ihn, es gibt nur
    noch keinen Bericht. Und eine falsche Meldung ist schlimmer als gar keine,
    weil der Anwender anfängt, seine Kachel zu reparieren (R145).
  */
  if (Object.keys(raum.bezuege).length === 0) {
    return { fehler: { schluessel: 'kachel.keinlauf' } }
  }

  if (kachel.quelle.art === 'gestellt') {
    const bezug = raum.bezuege[kachel.quelle.schluessel]
    if (!bezug) {
      return {
        fehler: {
          schluessel: 'formel.bezug.unbekannt',
          werte: { bezug: nameDerGroesse(kachel.quelle.schluessel) },
        },
      }
    }

    return {
      roh: bezug.wert,
      text: zeigeZahl(bezug.wert, kachel.einheit, bezug.waehrung ?? raum.waehrung),
      zustand: ampel(bezug.wert, kachel.schwellen) ?? bezug.zustand,
      ziel: bezug.ziel,
      rechnung: null,
      operanden: [bezug],
    }
  }

  const lesung = lies(kachel.quelle.ausdruck)
  if (istFehler(lesung)) {
    return { fehler: lesung.fehler }
  }

  const ergebnis = werteAus(lesung.knoten, formelraum(raum))
  if (istAuswertungsfehler(ergebnis)) {
    return { fehler: ergebnis.fehler }
  }

  const operanden = bezuegeIn(lesung.knoten)
    .map((s) => raum.bezuege[s])
    .filter((b): b is Kachelbezug => Boolean(b))

  return {
    roh: ergebnis.wert,
    text: zeigeZahl(ergebnis.wert, kachel.einheit, raum.waehrung),
    zustand: ampel(ergebnis.wert, kachel.schwellen) ?? 'fertig',
    // Eine Formelkachel führt dorthin, wo ihr erster Operand herkommt —
    // **ein Ziel hat sie immer** (R347). Ohne Operanden ist sie reine
    // Arithmetik und führt in ihr eigenes Blatt, das die Rechnung zeigt.
    ziel: operanden[0]?.ziel ?? null,
    rechnung: eingesetzteRechnung(lesung.knoten, raum),
    operanden,
  }
}

function eingesetzteRechnung(knoten: Knoten, raum: Kachelraum): string {
  // Dieselbe Zahlenschreibweise wie in den Kacheln daneben: Wer die Rechnung
  // aufklappt, soll seine eigene Zahl wiedererkennen.
  return eingesetzt(knoten, formelraum(raum), (wert) => betrag(wert))
}

function nameDerGroesse(schluessel: string): string {
  const g = findeGroesse(schluessel)
  return g ? t(g.schriftzug) : schluessel
}

/**
 * Die Ampel entsteht am Blatt und nicht im Ausdruck (R341).
 *
 * `null` heißt: keine Schwellen gesetzt — dann gilt der Zustand, den die Größe
 * selbst mitbringt, und die Kachel behauptet keine Bewertung, die niemand
 * vorgenommen hat.
 */
export function ampel(wert: number, schwellen: Schwellen): Zustandsart | null {
  if (schwellen.gruenAb === null && schwellen.gelbAb === null) {
    return null
  }

  const erreicht = (grenze: number | null): boolean => {
    if (grenze === null) {
      return false
    }

    return schwellen.richtung === 'hoch' ? wert >= grenze : wert <= grenze
  }

  if (erreicht(schwellen.gruenAb)) {
    return 'fertig'
  }

  if (erreicht(schwellen.gelbAb)) {
    return 'offen'
  }

  return 'blockiert'
}

export function zeigeZahl(wert: number, einheit: Einheitsart, waehrung: string | null): string {
  switch (einheit) {
    case 'betrag':
      return waehrung ? `${betrag(wert)} ${waehrung}` : betrag(wert)
    case 'prozent':
      return `${wert.toLocaleString(zahlenschema(), {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })} %`
    case 'ms':
      return t('kachel.einheit.ms', { ms: anzahlwort(Math.round(wert)) })
    default:
      return Math.round(wert).toLocaleString(zahlenschema())
  }
}

/* --- Die Anordnung -------------------------------------------------------- */

/**
 * Die Vorlage ab Werk (R346) — eine für alle.
 *
 * Sie ist bewusst klein: vier Kacheln, die jede Art einmal zeigen. Ein volles
 * Brett am ersten Tag lehrt niemanden, wie man es ändert; vier Kacheln und ein
 * sichtbarer Knopf *Kachel hinzufügen* tun es.
 *
 * **Die Vorlage je Rolle ist ausdrücklich nicht entschieden** (7.5 Abschnitt 7)
 * — sie kommt mit dem Rollenmodell aus Bereich 8.
 */
export function vorlage(rolle: string = ROLLE_KONZERNBUCHHALTER): Brett {
  return { fassung: 1, kacheln: (VORLAGEN[rolle] ?? VORLAGEN[ROLLE_KONZERNBUCHHALTER]!)() }
}

/* --- Die Rollen (7.5 Abschnitt 8, R350) ------------------------------------ */

/**
 * Die vier ab Werk.
 *
 * **Nur die vier tragen eine Vorlage, der Vorrat selbst ist offen** (R350):
 * Eine Rolle, die das Haus anlegt, ist eine Zeile in `core.rolle` und kommt
 * hier nie an. Sie bekommt die Vorlage des Konzernbuchhalters — **nicht ein
 * leeres Brett**: Wer „Leiter Konzernsteuern" anlegt, meint einen
 * Fachanwender, und ein leeres Brett wäre die Auskunft, das System halte ihn
 * für niemanden.
 */
export const ROLLE_KONZERNBUCHHALTER = 'ROL-KONZERNBUCHHALTER'
export const ROLLE_MELDER = 'ROL-MELDER'
export const ROLLE_PRUEFER = 'ROL-PRUEFER'
export const ROLLE_ADMINISTRATOR = 'ROL-ADMINISTRATOR'

/**
 * Was die erste Kachel zeigt — nicht entfernbar, aber nicht überall dieselbe
 * (R353, Fortschreibung von R337).
 *
 * **Der Prüfer hat keinen Arbeitsvorrat** (7.1 Abschnitt 6). Ihm die
 * Arbeitsliste hinzustellen hieße, ihm dauerhaft einen leeren Kasten zu
 * zeigen — und genau diese Behauptung wollte 7.1 vermeiden. Er bekommt
 * stattdessen, was ihn angeht: die freigegebenen Abschlüsse mit ihren
 * Prüfcodes.
 */
export type ErsteKachel = 'arbeitsliste' | 'abschluesse'

export function ersteKachel(rolle: string): ErsteKachel {
  return rolle === ROLLE_PRUEFER ? 'abschluesse' : 'arbeitsliste'
}

/**
 * Der Katalog folgt R155 — durch Weglassen (R354).
 *
 * **Die Prüffrage ist das Ziel, nicht der Rang:** Jede Kachel führt irgendwohin
 * (R347); führt sie in einen Bereich, den diese Rolle gar nicht bearbeitet,
 * ist sie eine Sackgasse mit Zahl. Deshalb entscheidet hier der **Zielbereich**
 * und keine Liste von Rechten — Rechte gehören an die API (Bereich 8) und
 * niemals an eine Bildschirmliste.
 */
const BEREICHE_JE_ROLLE: Record<string, readonly string[]> = {
  // 7.2 Abschnitt 3.3: Der Prüfer sieht vier Bereiche.
  [ROLLE_PRUEFER]: ['nachweis', 'berichte', 'konzernstruktur', 'stammdaten'],
  // Er liefert und sieht, was seine Lieferung betrifft.
  [ROLLE_MELDER]: ['datenimport', 'berichte', 'anhang', 'stammdaten', 'nachweis'],
}

export function darfZiel(rolle: string, ziel: Sprung | null): boolean {
  const erlaubt = BEREICHE_JE_ROLLE[rolle]
  return !erlaubt || !ziel || erlaubt.includes(ziel.bereich)
}

/** Die Größen, die dieser Rolle angeboten werden (R354). */
export function groessenFuer(rolle: string): readonly Groesse[] {
  return GROESSEN.filter((g) => darfZiel(rolle, g.ziel))
}

/**
 * Die Kachelarten, die dieser Rolle angeboten werden (R354).
 *
 * **Eine Art ohne Gegenstand wird nicht angeboten:** Für den Prüfer gibt es
 * keinen Fortschritt, den er bearbeiten könnte — ein Katalogeintrag, der beim
 * Anklicken eine Kachel ins Nichts anlegt, ist schlimmer als keiner.
 */
export function artenFuer(rolle: string): readonly Kachelart[] {
  return KACHELARTEN.filter((art) => art !== 'stand' || gegenstaendeFuer(rolle).length > 0)
}

/** Die Gegenstände einer Standkachel, die dieser Rolle etwas sagen (R354). */
export function gegenstaendeFuer(rolle: string): readonly Gegenstand[] {
  const ziele: Record<Gegenstand, Sprung> = {
    schritte: { bereich: 'abschluss' },
    lieferstand: { bereich: 'datenimport' },
    systemzustand: { bereich: 'betrieb' },
  }

  // Der Systemzustand gehört dem Administrator: Antwortzeiten sind kein
  // Gegenstand, an dem ein Fachanwender etwas tun kann (R347).
  return GEGENSTAENDE.filter(
    (g) =>
      darfZiel(rolle, ziele[g]) &&
      (g !== 'systemzustand' || rolle === ROLLE_ADMINISTRATOR),
  )
}

/* --- Die vier Vorlagen ----------------------------------------------------- */

/**
 * Je Rolle ein Brett, und jedes beginnt mit dem, was diese Rolle morgens
 * zuerst wissen will.
 *
 * Bewusst **klein**: Vier Kacheln lehren, dass man sie ändern kann; zwanzig
 * lehren, dass man das Brett hinnimmt.
 */
const VORLAGEN: Record<string, () => Kachel[]> = {
  // Der Konzernbuchhalter: Was blockiert, ob die Bilanz aufgeht, wie sich das
  // Vermögen entwickelt, wie weit die Konsolidierung ist.
  [ROLLE_KONZERNBUCHHALTER]: () => [
    neueKachel('zahl', 'v1', {
      quelle: { art: 'gestellt', schluessel: 'befunde.hart' },
      einheit: 'anzahl',
      breite: 3,
    }),
    neueKachel('zahl', 'v2', {
      quelle: { art: 'gestellt', schluessel: 'bilanz.differenz' },
      einheit: 'betrag',
      breite: 3,
    }),
    neueKachel('verlauf', 'v3', {
      // Das Anlagevermögen — eine Position, die es in jedem Bilanzplan gibt.
      // Fehlt sie einem Haus doch, sagt die Kachel das im Klartext (R340)
      // und steht nicht leer da.
      quelle: { art: 'gestellt', schluessel: 'bilanz.POS-A' },
      einheit: 'betrag',
      breite: 6,
    }),
    neueKachel('stand', 'v4', { gegenstand: 'schritte', breite: 6 }),
  ],

  // Der Melder: Seine Frage ist „ist meine Lieferung durch, und geht sie auf?"
  // — deshalb steht der Lieferstand oben und in voller Breite.
  [ROLLE_MELDER]: () => [
    neueKachel('stand', 'v1', { gegenstand: 'lieferstand', breite: 12 }),
    neueKachel('zahl', 'v2', {
      quelle: { art: 'gestellt', schluessel: 'bilanz.differenz' },
      einheit: 'betrag',
      breite: 6,
    }),
    neueKachel('verlauf', 'v3', {
      quelle: { art: 'gestellt', schluessel: 'bilanz.POS-A' },
      einheit: 'betrag',
      breite: 6,
    }),
  ],

  // Der Prüfer: Er liest. Seine erste Kachel sind die freigegebenen Abschlüsse
  // (R353); darunter zählt, ob die Nachweiskette hält und was gebucht wurde.
  [ROLLE_PRUEFER]: () => [
    neueKachel('zahl', 'v1', {
      quelle: { art: 'gestellt', schluessel: 'nachweis.eintraege' },
      einheit: 'anzahl',
      breite: 6,
    }),
    neueKachel('zahl', 'v2', {
      quelle: { art: 'gestellt', schluessel: 'lauf.buchungen' },
      einheit: 'anzahl',
      breite: 6,
    }),
    neueKachel('verlauf', 'v3', {
      quelle: { art: 'gestellt', schluessel: 'bilanz.POS-A' },
      einheit: 'betrag',
      breite: 12,
    }),
  ],

  // Der Administrator: Er sieht die Anlage, nicht den Abschluss.
  [ROLLE_ADMINISTRATOR]: () => [
    neueKachel('stand', 'v1', { gegenstand: 'systemzustand', breite: 12 }),
    neueKachel('zahl', 'v2', {
      quelle: { art: 'gestellt', schluessel: 'antwortzeit.p95' },
      einheit: 'ms',
      breite: 6,
      schwellen: { gruenAb: 500, gelbAb: 1000, richtung: 'niedrig' },
    }),
    neueKachel('zahl', 'v3', {
      quelle: { art: 'gestellt', schluessel: 'nachweis.eintraege' },
      einheit: 'anzahl',
      breite: 6,
    }),
  ],
}

export function neueKachel(art: Kachelart, id: string, teile: Partial<Kachel> = {}): Kachel {
  return {
    id,
    art,
    titel: null,
    quelle: { art: 'gestellt', schluessel: 'befunde.hart' },
    gegenstand: art === 'stand' ? 'schritte' : null,
    einheit: 'betrag',
    darstellung: 'balken',
    einheiten: 12,
    breite: art === 'zahl' ? 3 : 6,
    hoehe: 1,
    eingeklappt: false,
    herkunft: { folgtKontext: true },
    schwellen: { gruenAb: null, gelbAb: null, richtung: 'hoch' },
    ...teile,
  }
}

/** Eine Kennung, die zwei Kacheln desselben Bretts nie teilen. */
export function neueKennung(brett: Brett): string {
  let n = brett.kacheln.length + 1
  while (brett.kacheln.some((k) => k.id === `k${n}`)) {
    n += 1
  }

  return `k${n}`
}

export function fuegeEin(brett: Brett, kachel: Kachel): Brett {
  return { ...brett, kacheln: [...brett.kacheln, kachel] }
}

export function entferne(brett: Brett, id: string): Brett {
  return { ...brett, kacheln: brett.kacheln.filter((k) => k.id !== id) }
}

export function ersetze(brett: Brett, kachel: Kachel): Brett {
  return { ...brett, kacheln: brett.kacheln.map((k) => (k.id === kachel.id ? kachel : k)) }
}

export function verdopple(brett: Brett, id: string): Brett {
  const stelle = brett.kacheln.findIndex((k) => k.id === id)
  if (stelle < 0) {
    return brett
  }

  const vorbild = brett.kacheln[stelle]
  if (!vorbild) {
    return brett
  }

  const zwilling = { ...vorbild, id: neueKennung(brett) }
  const kacheln = [...brett.kacheln]
  // Direkt daneben und nicht ans Ende: Die zweite Kachel für einen anderen
  // Kreis will man neben der ersten sehen, sonst hätte man sie nicht
  // verdoppelt.
  kacheln.splice(stelle + 1, 0, zwilling)
  return { ...brett, kacheln }
}

/**
 * Eine Kachel um eine Stelle nach vorn oder hinten — der Klickweg neben dem
 * Ziehweg (R344, R306).
 *
 * Am Rand passiert nichts, und zwar still: Ein Knopf, der am Ende der Liste
 * eine Meldung auslöst, erzieht niemanden.
 */
export function verschiebe(brett: Brett, id: string, richtung: 'vor' | 'zurueck'): Brett {
  const stelle = brett.kacheln.findIndex((k) => k.id === id)
  if (stelle < 0) {
    return brett
  }

  const ziel = richtung === 'vor' ? stelle - 1 : stelle + 1
  if (ziel < 0 || ziel >= brett.kacheln.length) {
    return brett
  }

  const kacheln = [...brett.kacheln]
  const eine = kacheln[stelle]
  const andere = kacheln[ziel]
  if (!eine || !andere) {
    return brett
  }

  kacheln[stelle] = andere
  kacheln[ziel] = eine
  return { ...brett, kacheln }
}

/**
 * Ziehen: die gezogene Kachel landet **vor** der, auf der losgelassen wird.
 *
 * Dieselbe Bedeutung wie im Gliederungsbaum (R322): Die Stelle, auf die man
 * zeigt, ist die Stelle, an die es kommt.
 */
export function ziehe(brett: Brett, gezogen: string, auf: string): Brett {
  if (gezogen === auf) {
    return brett
  }

  const von = brett.kacheln.findIndex((k) => k.id === gezogen)
  const nach = brett.kacheln.findIndex((k) => k.id === auf)
  if (von < 0 || nach < 0) {
    return brett
  }

  const kacheln = [...brett.kacheln]
  const [kachel] = kacheln.splice(von, 1)
  if (!kachel) {
    return brett
  }

  kacheln.splice(nach, 0, kachel)
  return { ...brett, kacheln }
}

/* --- Gemerkt --------------------------------------------------------------- */

const SPEICHER = 'coko.kachelbrett'

/**
 * Liest die Anordnung des Anwenders — und fällt bei allem Zweifel auf die
 * Vorlage zurück.
 *
 * **Eine gemerkte Anordnung darf die Anwendung nie zerstören.** Ein Browser,
 * der den Speicher sperrt, eine Anordnung aus einer älteren Ausgabe, ein von
 * Hand verbogener Eintrag: In allen drei Fällen steht die Vorlage da und nicht
 * ein weißer Bildschirm.
 *
 * **Die eigene Anordnung schlägt jede Vorlage** (R352) — auch über einen
 * Rollenwechsel hinweg. Es gibt deshalb **ein** gemerktes Brett und nicht eines
 * je Rolle: Wer etwas verschoben hat, hat eine Aussage über seine Arbeit
 * gemacht; eine Vorlage ist eine Vermutung darüber, und die Aussage schlägt
 * die Vermutung. Wer die Vorlage seiner heutigen Rolle will, setzt zurück.
 */
export function liesBrett(rolle: string = ROLLE_KONZERNBUCHHALTER): Brett {
  try {
    const roh = window.localStorage.getItem(SPEICHER)
    if (!roh) {
      return vorlage(rolle)
    }

    const gelesen = JSON.parse(roh) as Brett
    if (gelesen?.fassung !== 1 || !Array.isArray(gelesen.kacheln)) {
      return vorlage(rolle)
    }

    // Jede Kachel durch den Bauplan: Was eine spätere Ausgabe ergänzt hat,
    // bekommt seinen Vorgabewert, statt undefiniert zu bleiben.
    return {
      fassung: 1,
      kacheln: gelesen.kacheln
        .filter((k): k is Kachel => Boolean(k) && KACHELARTEN.includes(k.art))
        .map((k) => neueKachel(k.art, k.id, k)),
    }
  } catch {
    return vorlage(rolle)
  }
}

export function speichereBrett(brett: Brett): void {
  try {
    window.localStorage.setItem(SPEICHER, JSON.stringify(brett))
  } catch {
    // Gemerkt wird dann nichts. Das ist kein Fehler, über den ein Anwender
    // etwas erfahren müsste — er merkt es beim nächsten Anmelden, und der
    // Ort in der Datenbank kommt mit Bereich 8.
  }
}

export function vergissBrett(): void {
  try {
    window.localStorage.removeItem(SPEICHER)
  } catch {
    // Siehe oben.
  }
}

/* --- Der Verlauf ----------------------------------------------------------- */

export type Verlaufspunkt = { periode: number; wert: number }

/**
 * Der Anfang eines Verlaufs: so viele Zeiteinheiten des Rasters vor dem Ende.
 *
 * Rückwärts im Raster und nicht in Monaten: Zwölf Quartale sind drei Jahre,
 * und eine Achse, die zwölf Quartale verspricht und drei Jahre zeigt, ist
 * falsch beschriftet. Die Rechnung läuft über Monatszahlen, weil eine Periode
 * `JJJJMM` ist und Monat 13 kein Monat.
 */
export function frueher(periode: number, raster: 'monat' | 'quartal' | 'jahr', einheiten: number): number {
  const schritte = Math.max(1, einheiten) - 1
  const monate = raster === 'monat' ? schritte : raster === 'quartal' ? schritte * 3 : schritte * 12

  const jahr = Math.floor(periode / 100)
  const monat = (periode % 100) - monate
  const versatz = Math.ceil((1 - monat) / 12)

  return (jahr - versatz) * 100 + (monat + versatz * 12)
}

/**
 * Die Punkte eines Verlaufs aus einem Periodenbericht.
 *
 * **Kein zweiter Rechenweg:** Es ist derselbe Endpunkt, den das Periodenbild
 * der Berichte benutzt, und dieselbe Zeile darin. Die Kachel sucht die
 * Position heraus und zeichnet, was da steht.
 */
export function verlaufspunkte(
  spalten: readonly { periode: number }[],
  zeilen: readonly { position: string; werte: readonly number[] }[],
  position: string,
): Verlaufspunkt[] | null {
  const zeile = zeilen.find((z) => z.position === position)
  if (!zeile) {
    return null
  }

  return spalten.map((s, i) => ({ periode: s.periode, wert: zeile.werte[i] ?? 0 }))
}

/**
 * Die Balkenhöhen in Prozent — die Nulllinie liegt dort, wo sie hingehört.
 *
 * Ein Verlauf mit negativen Werten (die GuV führt Aufwendungen negativ, R183)
 * darf nicht aussehen wie einer ohne: Die Nulllinie wandert, die Balken hängen
 * nach unten.
 */
export function balken(punkte: readonly Verlaufspunkt[]): {
  nulllinie: number
  streifen: { periode: number; wert: number; von: number; hoehe: number }[]
} {
  const werte = punkte.map((p) => p.wert)
  const hoch = Math.max(0, ...werte)
  const tief = Math.min(0, ...werte)
  const spanne = hoch - tief

  if (spanne === 0) {
    return {
      nulllinie: 100,
      streifen: punkte.map((p) => ({ periode: p.periode, wert: p.wert, von: 100, hoehe: 0 })),
    }
  }

  const anteil = (wert: number): number => ((hoch - wert) / spanne) * 100

  return {
    nulllinie: anteil(0),
    streifen: punkte.map((p) => {
      const oben = Math.min(anteil(0), anteil(p.wert))
      return {
        periode: p.periode,
        wert: p.wert,
        von: oben,
        hoehe: Math.abs(anteil(p.wert) - anteil(0)),
      }
    }),
  }
}
